import React, { useState, useEffect, useMemo } from 'react';
import { TRIP_STATUSES } from './trackingUtils';
import { apiService } from '../../../services/apiService';

const VehicleTrackingFilterPop = ({
  isOpen,
  onClose,
  anchorEl,
  filters,
  vehicles,
  onApplyFilters,
}) => {
  const [draft, setDraft] = useState({ ...filters });
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setDraft({ ...filters });
    }
  }, [isOpen, filters]);

  // Load filter options from API whenever filter opens
  useEffect(() => {
    if (!isOpen) return;
    Promise.all([
      apiService.getVehicleTypesDropdown(),
      apiService.getCustomerDropdown(),
      apiService.getDriverDropdown(),
    ])
      .then(([typesRes, custsRes, drvsRes]) => {
        setVehicleTypes(Array.isArray(typesRes?.data) ? typesRes.data : []);
        setCustomers(Array.isArray(custsRes?.data) ? custsRes.data : []);
        setDrivers(Array.isArray(drvsRes?.data) ? drvsRes.data : []);
      })
      .catch(() => {});
  }, [isOpen]);

  // Compute live match count against already-loaded fleet
  const matchingCount = useMemo(() => {
    return vehicles.filter((v) => {
      if (v.suspended) return false;
      if (draft.cat && v.cat !== draft.cat) return false;
      if (draft.type && v.type !== draft.type) return false;
      if (draft.cust && v.cust !== draft.cust) return false;
      if (draft.drv && v.drv !== draft.drv) return false;
      if (draft.vio === 'any' && (!v.vio || v.vio.length === 0)) return false;
      if (draft.vio === 'none' && v.vio && v.vio.length > 0) return false;
      if (draft.trip === 'offtrip') {
        if (!(v.st === 'moving' && !v.trip && v.st !== 'offline' && v.st !== 'inactive')) return false;
      } else if (draft.trip === 'none') {
        if (v.trip) return false;
      } else if (draft.trip && v.tripStatus !== draft.trip) {
        return false;
      }
      return true;
    }).length;
  }, [vehicles, draft]);

  if (!isOpen) return null;

  const isMobile = window.innerWidth <= 600;

  const handleReset = () => {
    setDraft({ cat: '', vio: '', type: '', trip: '', cust: '', drv: '' });
  };

  const handleApply = () => {
    onApplyFilters(draft);
    onClose();
  };

  // Calculate popover coordinates on desktop
  let popStyle = {};
  if (!isMobile && anchorEl) {
    const rect = anchorEl.getBoundingClientRect();
    const width = Math.min(420, window.innerWidth - 24);
    const left = Math.max(12, Math.min(window.innerWidth - width - 12, rect.right - width));
    const top = rect.bottom + 6;
    popStyle = {
      position: 'fixed',
      zIndex: 1500,
      width: `${width}px`,
      left: `${left}px`,
      top: `${top}px`,
      maxHeight: `${Math.max(260, window.innerHeight - rect.bottom - 20)}px`,
    };
  }

  return (
    <>
      <div
        className="fp-scrim"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1490,
          background: 'rgba(16, 5, 46, 0.32)',
        }}
      />
      <div
        className={`f-pop ${isMobile ? 'sheet' : ''}`}
        style={popStyle}
        role="dialog"
        aria-label="Filters"
      >
        {isMobile && <div className="fp-grab" />}
        <div className="fp-h">
          <b>Filters</b>
          <button type="button" className="link" onClick={handleReset}>
            Reset
          </button>
        </div>

        <div className="fp-b">
          {/* Category */}
          <div className="fp-f">
            <label>LTV / HTV</label>
            <div className="segbar sm fp-seg" role="group">
              <button
                type="button"
                className={!draft.cat ? 'on' : ''}
                onClick={() => setDraft((prev) => ({ ...prev, cat: '' }))}
              >
                All
              </button>
              <button
                type="button"
                className={draft.cat === 'LTV' ? 'on' : ''}
                onClick={() => setDraft((prev) => ({ ...prev, cat: 'LTV' }))}
              >
                LTV
              </button>
              <button
                type="button"
                className={draft.cat === 'HTV' ? 'on' : ''}
                onClick={() => setDraft((prev) => ({ ...prev, cat: 'HTV' }))}
              >
                HTV
              </button>
            </div>
          </div>

          {/* Violations */}
          <div className="fp-f">
            <label>Violations today</label>
            <div className="segbar sm fp-seg" role="group">
              <button
                type="button"
                className={!draft.vio ? 'on' : ''}
                onClick={() => setDraft((prev) => ({ ...prev, vio: '' }))}
              >
                All
              </button>
              <button
                type="button"
                className={draft.vio === 'any' ? 'on' : ''}
                onClick={() => setDraft((prev) => ({ ...prev, vio: 'any' }))}
              >
                With violations
              </button>
              <button
                type="button"
                className={draft.vio === 'none' ? 'on' : ''}
                onClick={() => setDraft((prev) => ({ ...prev, vio: 'none' }))}
              >
                None
              </button>
            </div>
          </div>

          {/* Vehicle Type — loaded from API */}
          <div className="fp-f">
            <label>Vehicle type</label>
            <select
              className="select"
              value={draft.type || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}
            >
              <option value="">All Types</option>
              {vehicleTypes.map((t) => (
                <option key={t.value} value={t.label}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Trip Status */}
          <div className="fp-f">
            <label>Trip status</label>
            <select
              className="select"
              value={draft.trip || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, trip: e.target.value }))}
            >
              <option value="">All Statuses</option>
              {TRIP_STATUSES.filter((t) => t !== 'Awaiting for Trip').map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              <option value="none">Awaiting for Trip</option>
              <option value="offtrip">Moving without trip</option>
            </select>
          </div>

          {/* Customer — loaded from API */}
          <div className="fp-f">
            <label>Customer</label>
            <select
              className="select"
              value={draft.cust || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, cust: e.target.value }))}
            >
              <option value="">All Customers</option>
              {customers.map((c) => {
                // Strip "(CostCenter)" suffix so value matches v.cust (raw customer name)
                const name = c.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
                return (
                  <option key={c.value} value={name}>
                    {c.label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Driver — loaded from API */}
          <div className="fp-f">
            <label>Driver</label>
            <select
              className="select"
              value={draft.drv || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, drv: e.target.value }))}
            >
              <option value="">All Drivers</option>
              {drivers.map((d) => (
                <option key={d.value} value={d.label}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="fp-foot">
          <button type="button" className="btn sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn sm primary" onClick={handleApply}>
            Show {matchingCount} {matchingCount === 1 ? 'vehicle' : 'vehicles'}
          </button>
        </div>
      </div>
    </>
  );
};

export default VehicleTrackingFilterPop;
